import { z } from 'zod';
import { entityIdSchema, nonEmptyTextSchema, sha256Schema, utcDateTimeSchema, } from '@kop/contracts';
const versionSchema = z.number().int().positive();
const optionalDateTimeSchema = utcDateTimeSchema.optional();
export const artifactKindsV2 = [
    'work-definition',
    'work-plan',
    'ad-hoc-risk-assessment',
    'work-permit',
    'work-induction',
    'safety-education',
    'tbm',
    'patrol',
    'closeout',
    'work-control-dossier',
];
export const artifactKindV2Schema = z.enum(artifactKindsV2);
export const artifactVersionRefSchema = z
    .object({
    artifactKind: artifactKindV2Schema,
    artifactId: entityIdSchema,
    versionId: entityIdSchema,
    revision: versionSchema,
    contentHash: sha256Schema,
    stateSnapshot: z.enum(['submitted', 'approved', 'locked', 'superseded']),
    relationship: z.enum([
        'required-by',
        'produced-by',
        'supersedes',
        'evidence-for',
        'derived-from',
    ]),
    pinnedAt: utcDateTimeSchema,
    versionUnknown: z.boolean().optional(),
})
    .strict();
export const approvalDecisionRefSchema = z
    .object({
    approvalId: entityIdSchema,
    target: artifactVersionRefSchema,
    mandateId: entityIdSchema,
    mandateVersion: versionSchema,
    decisionStateSnapshot: z.enum(['pending', 'approved', 'rejected', 'revoked', 'superseded']),
    latestDecisionSequence: z.number().int().nonnegative(),
    decidedAt: optionalDateTimeSchema,
})
    .strict();
export const sourceSnapshotRefSchema = z
    .object({
    sourceType: nonEmptyTextSchema.max(100),
    sourceId: entityIdSchema,
    sourceVersionId: entityIdSchema,
    contentHash: sha256Schema,
    capturedAt: utcDateTimeSchema,
})
    .strict();
export const evidenceRefV2Schema = z
    .object({
    manifestId: entityIdSchema,
    contentHash: sha256Schema,
    committedGeneration: z.number().int().nonnegative(),
    scope: z
        .object({
        tenantId: entityIdSchema,
        siteId: entityIdSchema,
        contractorId: entityIdSchema,
        caseId: entityIdSchema,
    })
        .strict(),
    role: nonEmptyTextSchema.max(100),
    lockedAt: optionalDateTimeSchema,
})
    .strict();
export const principalSnapshotV2Schema = z
    .object({
    principalId: entityIdSchema,
    principalType: z.enum(['member', 'external-worker', 'service']),
    displayName: nonEmptyTextSchema.max(100),
    organizationId: entityIdSchema,
    organizationName: nonEmptyTextSchema.max(200),
})
    .strict();
export const attestationRefSchema = z
    .object({
    attestationId: entityIdSchema,
    targetVersionId: entityIdSchema,
    targetContentHash: sha256Schema,
    verifiedAtServer: utcDateTimeSchema,
})
    .strict();
export const attestationV2Schema = z
    .object({
    attestationId: entityIdSchema,
    subject: z
        .object({
        subjectType: z.enum(['member', 'external-worker']),
        identityId: entityIdSchema.optional(),
        workerId: entityIdSchema,
        nameSnapshot: nonEmptyTextSchema.max(100),
        organizationSnapshot: z
            .object({ id: entityIdSchema, name: nonEmptyTextSchema.max(200) })
            .strict(),
    })
        .strict(),
    recordedBy: principalSnapshotV2Schema,
    method: z.enum(['authenticated-self', 'supervised-shared-device', 'verified-paper-attachment']),
    witness: principalSnapshotV2Schema.optional(),
    meaningCode: nonEmptyTextSchema.max(100),
    meaningTextSnapshot: nonEmptyTextSchema.max(500),
    target: z
        .object({
        artifactKind: artifactKindV2Schema,
        versionId: entityIdSchema,
        contentHash: sha256Schema,
        sectionId: entityIdSchema.optional(),
    })
        .strict(),
    challengeId: entityIdSchema,
    deviceSessionId: entityIdSchema,
    occurredAtClient: optionalDateTimeSchema,
    verifiedAtServer: utcDateTimeSchema,
    signatureImageEvidenceRef: evidenceRefV2Schema.optional(),
    paperEvidenceRef: evidenceRefV2Schema.optional(),
    languageCode: z
        .string()
        .trim()
        .regex(/^[a-z]{2,3}(?:-[A-Z]{2})?$/),
})
    .strict()
    .superRefine((value, context) => {
    if (value.method === 'authenticated-self' &&
        value.subject.identityId !== value.recordedBy.principalId) {
        context.addIssue({
            code: 'custom',
            path: ['recordedBy'],
            message: 'Self attestation must be recorded by the subject',
        });
    }
    if (value.method === 'supervised-shared-device' && !value.witness) {
        context.addIssue({
            code: 'custom',
            path: ['witness'],
            message: 'Shared-device attestation requires a witness',
        });
    }
    if (value.method === 'verified-paper-attachment' && !value.paperEvidenceRef) {
        context.addIssue({
            code: 'custom',
            path: ['paperEvidenceRef'],
            message: 'Paper attestation requires locked paper evidence',
        });
    }
    if (value.method === 'verified-paper-attachment' &&
        value.paperEvidenceRef &&
        !value.paperEvidenceRef.lockedAt) {
        context.addIssue({
            code: 'custom',
            path: ['paperEvidenceRef', 'lockedAt'],
            message: 'Paper evidence must be locked before verification',
        });
    }
});
export const contractorSnapshotV2Schema = z
    .object({
    contractorId: entityIdSchema,
    legalName: nonEmptyTextSchema.max(200),
    businessRegistrationNumber: nonEmptyTextSchema.max(40),
})
    .strict();
export const engagementSnapshotV2Schema = z
    .object({
    engagementId: entityIdSchema,
    contractorId: entityIdSchema,
    siteId: entityIdSchema,
    scopeSnapshot: nonEmptyTextSchema.max(1_000),
    startsOn: z.iso.date(),
    endsOn: z.iso.date(),
    statusSnapshot: z.enum(['pending', 'active', 'ended', 'suspended']),
})
    .strict();
export const externalAccessGrantSchema = z
    .object({
    grantId: entityIdSchema,
    caseId: entityIdSchema,
    contractorId: entityIdSchema,
    subjectEmailHash: sha256Schema,
    tokenHash: sha256Schema,
    allowedActions: z.array(z.enum(['edit-own', 'submit', 'stop', 'close-request'])).min(1),
    issuedBy: principalSnapshotV2Schema,
    issuedAt: utcDateTimeSchema,
    expiresAt: utcDateTimeSchema,
    consumedAt: optionalDateTimeSchema,
    revokedAt: optionalDateTimeSchema,
    authSessionId: entityIdSchema.optional(),
    promotedMembershipId: entityIdSchema.optional(),
})
    .strict();
export const triggerFlagsSchema = z
    .object({
    nonRoutine: z.boolean(),
    holiday: z.boolean(),
    emergency: z.boolean(),
    policyHighRisk: z.boolean(),
})
    .strict();
export const workControlCaseHeadV2Schema = z
    .object({
    schemaVersion: z.literal(2),
    caseId: entityIdSchema,
    version: versionSchema,
    scope: z
        .object({
        kind: z.literal('contractor'),
        tenantId: entityIdSchema,
        siteId: entityIdSchema,
        contractorId: entityIdSchema,
    })
        .strict(),
    owningContractorId: entityIdSchema,
    participatingContractorIds: z.array(entityIdSchema),
    contractorSnapshot: contractorSnapshotV2Schema,
    engagementSnapshots: z.array(engagementSnapshotV2Schema),
    accessMode: z.enum(['workspace-membership', 'email-invite', 'one-time-claim']),
    triggerFlags: triggerFlagsSchema,
    applicabilityDecision: z
        .object({
        policyId: entityIdSchema,
        policyVersion: versionSchema,
        decidedAt: utcDateTimeSchema,
        matchedRuleIds: z.array(entityIdSchema),
        reasonCodes: z.array(nonEmptyTextSchema.max(100)),
        requiredArtifactKinds: z.array(artifactKindV2Schema),
    })
        .strict(),
    currentWorkDefinitionRevisionId: entityIdSchema,
    currentArtifactRefs: z.array(artifactVersionRefSchema),
    currentApprovalRefs: z.array(approvalDecisionRefSchema),
    activeSessionId: entityIdSchema.optional(),
    phaseProjection: z.enum([
        'intake',
        'planning',
        'review',
        'prestart',
        'activation-ready',
        'active',
        'suspended',
        'closeout',
        'closed',
        'cancelled',
        'expired',
        'legacy-archive',
    ]),
    nextTaskProjection: z.string().trim().max(300).nullable(),
    legacy: z
        .object({ sourceCollection: nonEmptyTextSchema, sourceId: nonEmptyTextSchema })
        .strict()
        .optional(),
    createdAt: utcDateTimeSchema,
    updatedAt: utcDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.scope.contractorId !== value.owningContractorId ||
        value.contractorSnapshot.contractorId !== value.owningContractorId) {
        context.addIssue({
            code: 'custom',
            path: ['owningContractorId'],
            message: 'Case scope and contractor snapshot must identify the owning contractor',
        });
    }
    if (!value.participatingContractorIds.includes(value.owningContractorId)) {
        context.addIssue({
            code: 'custom',
            path: ['participatingContractorIds'],
            message: 'Owning contractor must participate in the case',
        });
    }
    if ((value.triggerFlags.emergency || value.triggerFlags.holiday) &&
        value.applicabilityDecision.reasonCodes.length === 0) {
        context.addIssue({
            code: 'custom',
            path: ['applicabilityDecision', 'reasonCodes'],
            message: 'Emergency and holiday decisions require a published reason code',
        });
    }
});
const workWindowSchema = z
    .object({
    windowId: entityIdSchema,
    startsAt: utcDateTimeSchema,
    endsAt: utcDateTimeSchema,
    timezone: nonEmptyTextSchema.max(100),
})
    .strict()
    .refine((value) => Date.parse(value.endsAt) > Date.parse(value.startsAt), {
    message: 'Window end must follow start',
    path: ['endsAt'],
});
export const workDefinitionRevisionSchema = z
    .object({
    revisionId: entityIdSchema,
    caseId: entityIdSchema,
    revision: versionSchema,
    previousRevisionId: entityIdSchema.optional(),
    contentHash: sha256Schema,
    workTitle: nonEmptyTextSchema.max(300),
    purposeAndScope: nonEmptyTextSchema.max(2_000),
    plannedWindows: z.array(workWindowSchema).min(1),
    areas: z
        .array(z
        .object({
        areaId: entityIdSchema,
        labelSnapshot: nonEmptyTextSchema,
        part: nonEmptyTextSchema,
        line: z.string().optional(),
        mapVersionId: entityIdSchema.optional(),
        locationMarker: z.string().optional(),
    })
        .strict())
        .min(1),
    equipment: z.array(z
        .object({
        equipmentId: entityIdSchema.optional(),
        nameSnapshot: nonEmptyTextSchema,
        registration: z.string().optional(),
        operatorRequirementRef: sourceSnapshotRefSchema.optional(),
    })
        .strict()),
    materials: z.array(z
        .object({
        materialId: entityIdSchema.optional(),
        nameSnapshot: nonEmptyTextSchema,
        msdsVersionRef: sourceSnapshotRefSchema.optional(),
    })
        .strict()),
    responsibilities: z.array(z
        .object({
        roleCode: nonEmptyTextSchema,
        organizationId: entityIdSchema,
        identityId: entityIdSchema.optional(),
        nameSnapshot: nonEmptyTextSchema,
        contactSnapshot: z.string().optional(),
    })
        .strict()),
    workers: z
        .array(z
        .object({
        workerId: entityIdSchema,
        workforceReference: z.string().optional(),
        organizationId: entityIdSchema,
        nameSnapshot: nonEmptyTextSchema,
        trade: z.string().optional(),
        plannedFrom: utcDateTimeSchema,
        plannedUntil: optionalDateTimeSchema,
    })
        .strict())
        .min(1),
    steps: z
        .array(z
        .object({
        stepId: entityIdSchema,
        sequence: z.number().int().positive(),
        description: nonEmptyTextSchema.max(1_000),
        processCode: z.string().optional(),
        areaIds: z.array(entityIdSchema).min(1),
        equipmentIds: z.array(entityIdSchema),
        hazards: z
            .array(z
            .object({
            hazardId: entityIdSchema,
            categoryCode: z.string().optional(),
            description: nonEmptyTextSchema,
            sourceRefs: z.array(sourceSnapshotRefSchema),
        })
            .strict())
            .min(1),
        controls: z
            .array(z
            .object({
            controlId: entityIdSchema,
            action: nonEmptyTextSchema,
            verificationMethod: nonEmptyTextSchema,
            sourceRefs: z.array(sourceSnapshotRefSchema),
        })
            .strict())
            .min(1),
    })
        .strict())
        .min(1),
    emergencyOrHolidayReason: z.string().trim().min(1).optional(),
    siteEmergencyPlanRef: sourceSnapshotRefSchema,
    createdBy: principalSnapshotV2Schema,
    createdAt: utcDateTimeSchema,
})
    .strict();
export const workPermitPayloadV2Schema = z
    .object({
    caseId: entityIdSchema,
    sourceWorkDefinition: sourceSnapshotRefSchema,
    permitTypeFlags: z.array(nonEmptyTextSchema.max(100)),
    requestedWindows: z.array(workWindowSchema).min(1),
    requestedAreaSnapshots: z
        .array(z.object({ areaId: entityIdSchema, label: nonEmptyTextSchema }).strict())
        .min(1),
    responsibilitySnapshots: z.array(z
        .object({
        roleCode: nonEmptyTextSchema,
        principal: principalSnapshotV2Schema.optional(),
        organizationId: entityIdSchema,
    })
        .strict()),
    requiredControls: z.array(z
        .object({
        requirementId: entityIdSchema,
        policySnapshotRef: sourceSnapshotRefSchema,
        positiveStatement: nonEmptyTextSchema,
        response: z.enum(['conforming', 'correction-required', 'not-applicable']),
        rationale: z.string().optional(),
        correctionActionRef: z.string().optional(),
        verifiedBy: principalSnapshotV2Schema.optional(),
        verifiedAt: optionalDateTimeSchema,
    })
        .strict()),
    conditionalSections: z.array(z
        .object({
        sectionCode: nonEmptyTextSchema,
        applicabilityDecisionRef: sourceSnapshotRefSchema,
        responses: z.record(z.string(), z.unknown()),
        measurementPlanRef: sourceSnapshotRefSchema.optional(),
    })
        .strict()),
    emergencyPlanSnapshot: sourceSnapshotRefSchema,
    relatedPlanRefs: z.array(artifactVersionRefSchema),
    riskAssessmentRef: artifactVersionRefSchema,
    contractorAcknowledgementRef: attestationRefSchema,
})
    .strict()
    .superRefine((value, context) => {
    value.relatedPlanRefs.forEach((ref, index) => {
        if (ref.artifactKind !== 'work-plan')
            context.addIssue({
                code: 'custom',
                path: ['relatedPlanRefs', index],
                message: 'Related plans must be work-plan artifacts',
            });
    });
    if (value.riskAssessmentRef.artifactKind !== 'ad-hoc-risk-assessment')
        context.addIssue({
            code: 'custom',
            path: ['riskAssessmentRef'],
            message: 'Risk assessment reference has the wrong artifact kind',
        });
});
export const artifactLockSchema = z
    .object({
    contentHash: sha256Schema,
    lockedAt: utcDateTimeSchema,
    lockedBy: principalSnapshotV2Schema,
})
    .strict();
export const workInductionRecordV2Schema = z
    .object({
    inductionId: entityIdSchema,
    caseId: entityIdSchema,
    version: versionSchema,
    sourceWorkDefinition: sourceSnapshotRefSchema,
    sourcePermitRef: artifactVersionRefSchema,
    sourceRiskAssessmentRef: artifactVersionRefSchema,
    classification: z.enum(['operational-briefing', 'mapped-safety-education']),
    educationMappingRef: sourceSnapshotRefSchema.optional(),
    sessions: z
        .array(z
        .object({
        startsAt: utcDateTimeSchema,
        endsAt: utcDateTimeSchema,
        location: nonEmptyTextSchema,
        instructor: principalSnapshotV2Schema,
        deliveredTopicIds: z.array(entityIdSchema),
        materialVersionRefs: z.array(sourceSnapshotRefSchema),
    })
        .strict())
        .min(1),
    targetWorkerIds: z.array(entityIdSchema).min(1),
    attendance: z.array(z
        .object({
        workerId: entityIdSchema,
        status: z.enum(['attended', 'excluded']),
        attestationRef: attestationRefSchema.optional(),
        exclusionReason: z.string().optional(),
    })
        .strict()),
    photoEvidenceRefs: z.array(evidenceRefV2Schema),
    questionsAndResponses: z.array(z
        .object({
        question: nonEmptyTextSchema,
        response: nonEmptyTextSchema,
        respondedBy: entityIdSchema,
    })
        .strict()),
    lock: artifactLockSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.sourcePermitRef.artifactKind !== 'work-permit')
        context.addIssue({
            code: 'custom',
            path: ['sourcePermitRef'],
            message: 'Induction must pin a permit version',
        });
    if (value.sourceRiskAssessmentRef.artifactKind !== 'ad-hoc-risk-assessment')
        context.addIssue({
            code: 'custom',
            path: ['sourceRiskAssessmentRef'],
            message: 'Induction must pin a risk assessment version',
        });
    if (value.classification === 'mapped-safety-education' && !value.educationMappingRef) {
        context.addIssue({
            code: 'custom',
            path: ['educationMappingRef'],
            message: 'Mapped education requires an approved mapping reference',
        });
    }
    for (const [index, item] of value.attendance.entries()) {
        if (item.status === 'attended' && !item.attestationRef)
            context.addIssue({
                code: 'custom',
                path: ['attendance', index, 'attestationRef'],
                message: 'Attendance requires an attestation',
            });
        if (item.status === 'excluded' && !item.exclusionReason)
            context.addIssue({
                code: 'custom',
                path: ['attendance', index, 'exclusionReason'],
                message: 'Exclusion requires a reason',
            });
    }
});
export const permitSessionStates = [
    'planned',
    'activation-ready',
    'active',
    'suspended',
    'completion-requested',
    'closed',
    'expired',
    'cancelled',
];
export const permitSessionStateSchema = z.enum(permitSessionStates);
export const permitSessionHeadSchema = z
    .object({
    sessionId: entityIdSchema,
    caseId: entityIdSchema,
    version: versionSchema,
    state: permitSessionStateSchema,
    permitRef: artifactVersionRefSchema,
    riskAssessmentRef: artifactVersionRefSchema,
    requiredPlanRefs: z.array(artifactVersionRefSchema),
    inductionRef: artifactVersionRefSchema,
    tbmRef: artifactVersionRefSchema,
    policySnapshotRef: sourceSnapshotRefSchema,
    originalStartsAt: utcDateTimeSchema,
    originalEndsAt: utcDateTimeSchema,
    effectiveStartsAt: utcDateTimeSchema,
    effectiveEndsAt: utcDateTimeSchema,
    activatedAt: optionalDateTimeSchema,
    activatedBy: principalSnapshotV2Schema.optional(),
    clearedRosterRevision: z.number().int().nonnegative(),
    clearedWorkerIds: z.array(entityIdSchema),
    openStopEventId: entityIdSchema.optional(),
    openChangeRequestIds: z.array(entityIdSchema),
    pendingExtensionRequestIds: z.array(entityIdSchema),
    lastEventSequence: z.number().int().nonnegative(),
    updatedAt: utcDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (Date.parse(value.originalEndsAt) <= Date.parse(value.originalStartsAt))
        context.addIssue({
            code: 'custom',
            path: ['originalEndsAt'],
            message: 'Original end must follow start',
        });
    if (Date.parse(value.effectiveEndsAt) < Date.parse(value.originalEndsAt))
        context.addIssue({
            code: 'custom',
            path: ['effectiveEndsAt'],
            message: 'Effective end cannot shorten the original window',
        });
    if (value.state === 'active' && (!value.activatedAt || !value.activatedBy))
        context.addIssue({
            code: 'custom',
            path: ['activatedAt'],
            message: 'Active session requires activation authority',
        });
    if (value.state === 'suspended' && !value.openStopEventId)
        context.addIssue({
            code: 'custom',
            path: ['openStopEventId'],
            message: 'Suspended session requires an open stop event',
        });
    const kindChecks = [
        ['work-permit', value.permitRef, 'permitRef'],
        ['ad-hoc-risk-assessment', value.riskAssessmentRef, 'riskAssessmentRef'],
        ['work-induction', value.inductionRef, 'inductionRef'],
        ['tbm', value.tbmRef, 'tbmRef'],
    ];
    value.requiredPlanRefs.forEach((ref, index) => kindChecks.push(['work-plan', ref, `requiredPlanRefs.${index}`]));
    for (const [expected, ref, path] of kindChecks) {
        if (ref.artifactKind !== expected)
            context.addIssue({
                code: 'custom',
                path: path.split('.'),
                message: `Expected ${expected} artifact`,
            });
    }
});
export const workControlEventTypes = [
    'session-ready',
    'session-activated',
    'patrol-recorded',
    'correction-recorded',
    'work-stopped',
    'resume-requested',
    'work-resumed',
    'extension-requested',
    'extension-approved',
    'extension-rejected',
    'change-requested',
    'change-approved',
    'change-rejected',
    'worker-added',
    'worker-removed',
    'worker-cleared',
    'atmospheric-measurement-recorded',
    'atmospheric-measurement-deviation',
    'completion-requested',
    'closeout-returned',
    'closeout-confirmed',
    'session-expired',
    'session-cancelled',
];
export const workControlEventTypeSchema = z.enum(workControlEventTypes);
export const workControlEventV2Schema = z
    .object({
    eventId: entityIdSchema,
    caseId: entityIdSchema,
    sessionId: entityIdSchema,
    sequence: z.number().int().positive(),
    eventType: workControlEventTypeSchema,
    actor: principalSnapshotV2Schema,
    occurredAtClient: optionalDateTimeSchema,
    recordedAtServer: utcDateTimeSchema,
    reasonCode: z.string().trim().min(1).max(100).optional(),
    reasonText: z.string().trim().min(1).max(2_000).optional(),
    sourceVersionRefs: z.array(sourceSnapshotRefSchema),
    evidenceRefs: z.array(evidenceRefV2Schema),
    idempotencyKey: entityIdSchema,
    commandFingerprint: sha256Schema,
    correlationId: entityIdSchema,
    causationId: entityIdSchema.optional(),
    typePayload: z.record(z.string(), z.unknown()),
})
    .strict();
export const atmosphericMeasurementRecordSchema = z
    .object({
    measurementId: entityIdSchema,
    caseId: entityIdSchema,
    sessionId: entityIdSchema,
    sequence: z.number().int().positive(),
    applicabilityDecisionRef: sourceSnapshotRefSchema,
    measurementPlanVersionRef: sourceSnapshotRefSchema,
    areaSnapshot: nonEmptyTextSchema.max(300),
    pointSnapshot: nonEmptyTextSchema.max(300),
    measuredAt: utcDateTimeSchema,
    measuredBy: principalSnapshotV2Schema,
    instrument: z
        .object({
        instrumentId: entityIdSchema,
        modelSnapshot: z.string().optional(),
        calibrationEvidenceRef: evidenceRefV2Schema.optional(),
    })
        .strict(),
    readings: z
        .array(z
        .object({
        analyteCode: nonEmptyTextSchema.max(100),
        value: z.number().finite(),
        unit: nonEmptyTextSchema.max(40),
        criterionSnapshotRef: sourceSnapshotRefSchema.optional(),
        criterionDisplaySnapshot: z.string().optional(),
        evaluation: z.enum(['conforming', 'deviation', 'not-evaluated']),
    })
        .strict())
        .min(1),
    environmentNote: z.string().optional(),
    evidenceRefs: z.array(evidenceRefV2Schema),
    recordedAtServer: utcDateTimeSchema,
})
    .strict()
    .superRefine((value, context) => {
    value.readings.forEach((reading, index) => {
        if (!reading.criterionSnapshotRef && reading.evaluation !== 'not-evaluated')
            context.addIssue({
                code: 'custom',
                path: ['readings', index, 'evaluation'],
                message: 'A reading without a published criterion must remain not-evaluated',
            });
    });
});
export const closeoutRecordV2Schema = z
    .object({
    closeoutId: entityIdSchema,
    caseId: entityIdSchema,
    sessionId: entityIdSchema,
    version: versionSchema,
    requestedBy: principalSnapshotV2Schema,
    requestedAt: utcDateTimeSchema,
    workerExit: z.array(z
        .object({
        workerId: entityIdSchema,
        confirmed: z.boolean(),
        exitedAt: optionalDateTimeSchema,
    })
        .strict()),
    equipmentAndToolRecovery: z.boolean(),
    housekeeping: z.boolean(),
    facilityDamage: z.enum(['none', 'found-and-recorded']),
    damageFindingRefs: z.array(artifactVersionRefSchema),
    completionTime: utcDateTimeSchema,
    contractorAttestationRef: attestationRefSchema,
    principalInspection: z
        .object({
        inspector: principalSnapshotV2Schema,
        result: z.enum(['confirmed', 'returned']),
        opinion: nonEmptyTextSchema,
        inspectedAt: utcDateTimeSchema,
    })
        .strict(),
    evidenceRefs: z.array(evidenceRefV2Schema),
    approvalRef: approvalDecisionRefSchema.optional(),
    lock: artifactLockSchema,
})
    .strict();
export const evidencePackageManifestV2Schema = z
    .object({
    packageId: entityIdSchema,
    caseId: entityIdSchema,
    packageVersion: versionSchema,
    artifactRefs: z.array(artifactVersionRefSchema).min(1),
    eventRange: z
        .object({
        firstSequence: z.number().int().positive(),
        lastSequence: z.number().int().positive(),
        eventMerkleRoot: sha256Schema,
    })
        .strict(),
    evidenceRefs: z.array(z
        .object({
        manifestId: entityIdSchema,
        sourceSha256: sha256Schema,
        committedGeneration: z.number().int().nonnegative(),
    })
        .strict()),
    printArtifactRefs: z.array(artifactVersionRefSchema),
    canonicalizationVersion: versionSchema,
    packageHash: sha256Schema,
    lockedAt: utcDateTimeSchema,
    lockedBy: principalSnapshotV2Schema,
    correctionOfPackageId: entityIdSchema.optional(),
})
    .strict()
    .refine((value) => value.eventRange.lastSequence >= value.eventRange.firstSequence, {
    path: ['eventRange', 'lastSequence'],
    message: 'Event range must be ordered',
});
/** Transitional adapter only. Unknown versions must never satisfy authoritative readiness. */
export function legacyArtifactRef(input) {
    return {
        artifactKind: input.artifactKind,
        artifactId: input.documentId,
        versionId: `legacy-unknown-${input.documentId}`,
        revision: 1,
        contentHash: '0'.repeat(64),
        stateSnapshot: 'superseded',
        relationship: 'derived-from',
        pinnedAt: input.pinnedAt,
        versionUnknown: true,
    };
}
/** Compatibility bridge for internal v1 ActorSnapshot producers. */
export function principalFromActor(actor) {
    return {
        principalId: actor.identityId,
        principalType: actor.organization.organizationType === 'contractor' ? 'external-worker' : 'member',
        displayName: actor.displayName,
        organizationId: actor.organization.organizationId,
        organizationName: actor.organization.organizationName,
    };
}
